import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Calendar, Pencil, Star, Plus, ArrowRightCircle, Sparkles, Trash2 } from 'lucide-react';
import { timeAgo } from '../utils/relativeTime';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

const Seasons = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | seasonObj
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '', status: 'upcoming' });
  const [submitting, setSubmitting] = useState(false);
  const [rollTarget, setRollTarget] = useState(null); // season obj being rolled
  const [rollError, setRollError] = useState('');
  const [rolling, setRolling] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/seasons`, h);
      setSeasons(r.data || []);
    } catch {
      toast.error('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setForm({ name: '', start_date: '', end_date: '', status: 'upcoming' });
    setEditing('new');
  };
  const openEdit = (s) => {
    setForm({ name: s.name, start_date: s.start_date, end_date: s.end_date, status: s.status });
    setEditing(s);
  };

  const submit = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error('Name, start and end dates are required.');
      return;
    }
    setSubmitting(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/api/seasons`, form, h);
        toast.success('Season created.');
      } else {
        await axios.put(`${API}/api/seasons/${editing.id}`, form, h);
        toast.success('Season updated.');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save season');
    } finally {
      setSubmitting(false);
    }
  };

  const makeCurrent = async (s) => {
    if (!window.confirm(`Make "${s.name}" the current season? All operational defaults will switch.`)) return;
    try {
      await axios.post(`${API}/api/seasons/${s.id}/set-current`, {}, h);
      toast.success(`${s.name} is now current.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set current');
    }
  };

  // Phase 65c — compute next season preview (mirrors backend: +1 year, Aug 1 → Jul 31).
  const computeNextSeason = (s) => {
    if (!s?.start_date) return null;
    const y = parseInt(s.start_date.slice(0, 4), 10);
    if (Number.isNaN(y)) return null;
    const next = y + 1;
    return {
      name: `${next}/${String(next + 1).slice(-2)} Season`,
      start_date: `${next}-08-01`,
      end_date: `${next + 1}-07-31`,
    };
  };

  const openRoll = (s) => {
    setRollError('');
    setRollTarget(s);
  };

  const deleteSeason = async (s) => {
    if (s.is_current) { toast.error('Cannot delete the current season.'); return; }
    const linkCount = s.athlete_count ?? 0;
    const warn = linkCount > 0
      ? `Delete "${s.name}"? It has ${linkCount} athlete link(s) which will be removed (athletes are NOT deleted).`
      : `Delete "${s.name}"? This cannot be undone. Athletes are not affected.`;
    if (!window.confirm(warn)) return;
    try {
      await axios.delete(`${API}/api/seasons/${s.id}${linkCount > 0 ? '?cascade_links=true' : ''}`, h);
      toast.success(`Deleted ${s.name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete season');
    }
  };

  const runCleanup = async () => {
    setCleanupBusy(true);
    setCleanupResult(null);
    try {
      const r = await axios.post(`${API}/api/seasons/cleanup-standard`, {}, h);
      setCleanupResult(r.data);
      toast.success(
        `Cleaned: ${r.data.seasons_before} → ${r.data.seasons_after} seasons, ${r.data.seasons_deleted} junk removed`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleanupBusy(false);
    }
  };

  const confirmRoll = async () => {
    if (!rollTarget) return;
    setRolling(true);
    setRollError('');
    try {
      const r = await axios.post(`${API}/api/seasons/${rollTarget.id}/roll-forward`, {}, h);
      toast.success(`Rolled forward → ${r.data?.name || 'next season'}.`);
      setRollTarget(null);
      await load();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Failed to roll forward';
      if (status === 409) {
        setRollError(detail);
      } else if (status === 400) {
        setRollError(`Validation error: ${detail}`);
      } else {
        setRollError(detail);
      }
    } finally {
      setRolling(false);
    }
  };

  const currentSeason = seasons.find((s) => s.is_current);

  return (
    <Layout>
      <div className="space-y-4" data-testid="seasons-page">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Calendar className="text-emerald-600" size={22} />
              Seasons
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Academy seasons run <strong>July 1 to June 30</strong>. The current season drives operational defaults across analytics surfaces.
            </p>
            {currentSeason && (
              <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1" data-testid="current-season-banner">
                Current Season: <strong>{currentSeason.name}</strong> ({currentSeason.start_date} → {currentSeason.end_date})
              </p>
            )}
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => { setCleanupResult(null); setCleanupOpen(true); }}
                className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30"
                data-testid="seasons-cleanup-btn"
              >
                <Sparkles size={14} className="mr-1" /> Clean Seasons
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.assign('/seasons/transition')}
                className="border-pfap-green/40 text-pfap-green hover:bg-pfap-green/5"
                data-testid="seasons-transition-btn"
              >
                <ArrowRightCircle size={14} className="mr-1" /> Transition to new season
              </Button>
              <Button onClick={openNew} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="seasons-new-btn">
                <Plus size={14} className="mr-1" /> New season
              </Button>
            </div>
          )}
        </div>

        <Card data-testid="seasons-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">All seasons ({seasons.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-xs text-gray-400 text-center py-6">Loading…</p>
            ) : seasons.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">No seasons — click "New season" to add one.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {seasons.map((s) => (
                  <li key={s.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`season-row-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{s.name}</p>
                        {s.is_current ? (
                          <Badge className="bg-emerald-500 text-white text-[10px]" data-testid={`season-current-${s.id}`}>Current</Badge>
                        ) : null}
                        <Badge className={`${STATUS_STYLES[s.status] || ''} text-[10px]`}>{s.status}</Badge>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {s.start_date} → {s.end_date} · {s.athlete_count ?? 0} athletes on roster · created {timeAgo(s.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {s.is_current && isAdmin ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                          onClick={() => openRoll(s)}
                          data-testid={`season-roll-forward-${s.id}`}
                        >
                          <ArrowRightCircle size={12} className="mr-1" /> Roll to next season
                        </Button>
                      ) : null}
                      {!s.is_current && isAdmin ? (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => makeCurrent(s)} data-testid={`season-make-current-${s.id}`}>
                          <Star size={12} className="mr-1" /> Make current
                        </Button>
                      ) : null}
                      {isAdmin ? (
                        <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => openEdit(s)} data-testid={`season-edit-${s.id}`}>
                          <Pencil size={12} className="mr-1" /> Edit
                        </Button>
                      ) : null}
                      {isAdmin && !s.is_current ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                          onClick={() => deleteSeason(s)}
                          data-testid={`season-delete-${s.id}`}
                        >
                          <Trash2 size={12} className="mr-1" /> Delete
                        </Button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-md" data-testid="seasons-dialog">
            <DialogHeader>
              <DialogTitle>{editing === 'new' ? 'Create season' : 'Edit season'}</DialogTitle>
              <DialogDescription className="sr-only">Manage season metadata.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., 2026/27 Season" data-testid="season-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start date *</Label>
                  <DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="season-start-date" />
                </div>
                <div>
                  <Label>End date *</Label>
                  <DatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="season-end-date" />
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="season-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="season-save-btn">
                {submitting ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 65c — Roll forward confirmation */}
        <Dialog
          open={!!rollTarget}
          onOpenChange={(o) => {
            if (!o && !rolling) {
              setRollTarget(null);
              setRollError('');
            }
          }}
        >
          <DialogContent className="max-w-md" data-testid="season-roll-forward-dialog">
            <DialogHeader>
              <DialogTitle>Roll to next season</DialogTitle>
              <DialogDescription className="sr-only">Create the next chronological season and carry active athlete links forward.</DialogDescription>
            </DialogHeader>
            {rollTarget ? (
              <div className="space-y-3 py-1 text-sm">
                <div className="rounded-md border border-gray-200 dark:border-gray-800 p-3 bg-gray-50 dark:bg-gray-900/40">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Current season</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100" data-testid="roll-forward-current-name">
                    {rollTarget.name}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {rollTarget.start_date} → {rollTarget.end_date}
                  </p>
                </div>

                {(() => {
                  const next = computeNextSeason(rollTarget);
                  if (!next) {
                    return (
                      <p className="text-xs text-red-600" data-testid="roll-forward-invalid-source">
                        Source season has an invalid start date; cannot compute the next season.
                      </p>
                    );
                  }
                  return (
                    <div className="rounded-md border border-emerald-200 dark:border-emerald-800 p-3 bg-emerald-50 dark:bg-emerald-900/30">
                      <p className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Will create</p>
                      <p className="font-semibold text-emerald-900 dark:text-emerald-100" data-testid="roll-forward-next-name">
                        {next.name}
                      </p>
                      <p className="text-[11px] text-emerald-800/80 dark:text-emerald-200/80 mt-0.5" data-testid="roll-forward-next-range">
                        {next.start_date} → {next.end_date}
                      </p>
                    </div>
                  );
                })()}

                <p className="text-xs text-gray-600 dark:text-gray-300" data-testid="roll-forward-explanation">
                  All athletes currently <strong>active</strong> in <em>{rollTarget.name}</em> will be carried forward as active links in the new season. The current season will be archived and the new season will become current. Historical data is not modified.
                </p>

                {rollError ? (
                  <p className="text-xs text-red-600 border border-red-200 bg-red-50 dark:bg-red-900/30 dark:border-red-800 rounded-md p-2" data-testid="roll-forward-error">
                    {rollError}
                  </p>
                ) : null}
              </div>
            ) : null}
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => { setRollTarget(null); setRollError(''); }}
                disabled={rolling}
                data-testid="roll-forward-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRoll}
                disabled={rolling || !computeNextSeason(rollTarget)}
                className="bg-pfap-green hover:bg-pfap-green/90"
                data-testid="roll-forward-confirm-btn"
              >
                {rolling ? 'Rolling…' : 'Confirm roll forward'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Standard cleanup dialog — Admin only */}
        <Dialog open={cleanupOpen} onOpenChange={(o) => { if (!o) { setCleanupOpen(false); setCleanupResult(null); } }}>
          <DialogContent className="max-w-lg" data-testid="seasons-cleanup-dialog">
            <DialogHeader>
              <DialogTitle>Clean Seasons</DialogTitle>
              <DialogDescription>
                This will remove test/junk season records from the live database and keep only the standard 2025/26–2029/30 seasons. It will <strong>not</strong> delete athletes or any module data.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-xs text-gray-600 dark:text-gray-300">
              <ul className="list-disc list-inside space-y-1">
                <li>Deletes seasons named <code>TEST_*</code>, <code>QA_*</code>, <code>(TEST)</code>, and other duplicates.</li>
                <li>Preserves the existing current-season id where possible (athlete links stay valid).</li>
                <li>Repairs orphan athlete links by re-pointing them at the canonical 2025/26 row.</li>
                <li>Idempotent — running it twice yields the same result.</li>
              </ul>
              {cleanupResult && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 p-3 space-y-1" data-testid="seasons-cleanup-result">
                  <p className="text-xs text-emerald-800 dark:text-emerald-200">
                    <strong>Result:</strong> {cleanupResult.seasons_before} → {cleanupResult.seasons_after} seasons,
                    {' '}{cleanupResult.seasons_deleted} junk removed,
                    {' '}{cleanupResult.links_repaired} links repaired,
                    {' '}{cleanupResult.links_removed} duplicate links removed.
                  </p>
                  <p className="text-[11px] text-emerald-700 dark:text-emerald-300">
                    {cleanupResult.ok ? '✓ Exactly 5 seasons, 1 current. Catalogue clean.' : '⚠ Integrity check did not match expected state — review above counts.'}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => { setCleanupOpen(false); setCleanupResult(null); }}
                disabled={cleanupBusy}
                data-testid="seasons-cleanup-cancel-btn"
              >
                {cleanupResult ? 'Close' : 'Cancel'}
              </Button>
              {!cleanupResult && (
                <Button
                  onClick={runCleanup}
                  disabled={cleanupBusy}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="seasons-cleanup-confirm-btn"
                >
                  <Trash2 size={14} className="mr-1" /> {cleanupBusy ? 'Cleaning…' : 'Yes, clean seasons'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Seasons;
